import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const SystemArchitecture: React.FC = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeHotspot, setActiveHotspot] = useState<string | null>(null);
  const diagramContainerRef = useRef<HTMLDivElement>(null);

  // PI filter state - all selected by default
  const [piFilters, setPiFilters] = useState({
    PI5: true,
    PI6: true,
    Future: true
  });

  // Voice selection state
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');

  // Auto-play tour state (for diagram)
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTourIndex, setCurrentTourIndex] = useState(-1);
  const [isMuted, setIsMuted] = useState(false);
  const playIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const speechRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Page-level tour state
  const [isPageTourPlaying, setIsPageTourPlaying] = useState(false);
  const [currentPageTourIndex, setCurrentPageTourIndex] = useState(-1);
  const pageTourIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pageSpeechRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isPageTourPlayingRef = useRef(isPageTourPlaying);
  const currentPageTourIndexRef = useRef(currentPageTourIndex);

  // Voice narration for each component - provides context beyond screen text
  const narrations: Record<string, string> = {
    'user': "Let's start with the end user completing personnel security questionnaires. What's different here is AI-powered assistance at every step - intelligent field guidance that explains what's needed, real-time validation that catches errors before submission, and FAQ answers pulled directly from policy documents with citations.",
    'frontend': "The React frontend delivers AI capabilities directly to the user - real-time validation feedback, context-aware FAQ assistance, intelligent field help, and comprehensive AI review before submission. Every interaction is designed to reduce ambiguity and catch errors at the source. NGINX ensures fast, reliable delivery of the application.",
    'api-gateway': "Here we have the Go API layer for business logic and data persistence.  Using Go gives us exceptional performance and low memory overhead.",
    'vpc-lattice': "VPC Lattice is your service mesh gateway for secure DoD system integration. External systems can access the AI capabilities like FAQ RAG, AI Field Assist, and future API services, but also our rich analytics data including real-time NOC metrics and the historical S3 Parquet data lake. This unlocks capabilities for the enterprise - security operations centers can tap into our monitoring data, analyst teams can query our data lake for trend analysis, and enterprise intelligence systems can integrate our AI insights, all without VPC peering complexity or compromising security. VPC Lattice enforces Zero Trust principles with IAM policy enforcement on every request, provides service-level access control so we can expose specific functions and data sets granularly, and keeps all traffic on the AWS private backbone with no internet exposure. For DoD environments with strict egress controls, this is the clean, auditable way to share both AI services and analytics data across enclaves and accounts.",
    'fastapi-container': "This FastAPI container is the control center for AI operations. It orchestrates five different AI-powered services - from answering FAQs to comprehensive reviews. The microservices architecture allows each function to scale independently based on demand.",
    'bedrock': "AWS Bedrock represents our AI foundation. We strategically use three different models: Nova Pro for complex analysis requiring deep reasoning, Nova Lite for quick field assistance where speed matters, and Titan for converting documents into searchable vector embeddings. The Guardrails layer automatically blocks inappropriate content and redacts personally identifiable information before it ever reaches the models.",
    'elasticsearch': "The ElasticSearch vector database is what makes our FAQ system so accurate. When a user asks a question, we convert it to vectors and search through thousands of policy documents in milliseconds. This is the secret sauce behind contextual, policy-compliant responses.",
    'sns-topic': "SNS Topics enable our intelligent event-driven architecture. When an audit event gets published, it fans out to multiple subscribers including our NOC Monitor Lambda, which does something particularly valuable: it uses Nova Lite, a small language model, to intelligently assess each event's risk level. Not every event needs this evaluation - we optimize costs by selectively classifying blocked events, high-frequency users, and a sample of normal traffic. This smart routing means we get AI-powered threat assessment where it matters most, while the full event stream flows to both real-time storage and analytics pipelines. It's the pub-sub pattern enhanced with intelligent event processing.",
    'dynamodb-audit': "The Audit Records table is our source of truth. Every interaction, every validation, every guardrail trigger gets recorded here with microsecond timestamps. This creates an immutable audit trail crucial for compliance and security investigations.",
    'dynamodb-noc': "The NOC Metrics table stores pre-aggregated data enriched with AI classifications. Instead of querying raw events millions of times, we calculate rolling averages and percentiles once, but here's what makes this table special: it also stores the Nova Lite risk assessments. Each classified event includes a risk score from zero to one hundred, the classification label, and most importantly, the reasoning behind that classification. This means when an operator investigates an alert, they're not just seeing that something triggered - they understand why the AI flagged it as concerning. This explainability is crucial for building operator trust and making informed security decisions. It's how we achieve sub-second dashboard response times with intelligent, contextual alerts.",
    'noc-soc': "The NOC dashboard gives operators real-time visibility with intelligent alert prioritization. When error rates spike or suspicious patterns emerge, alerts don't just trigger - they arrive with AI-generated risk assessments and explanations. An operator sees not just that a user exceeded rate limits, but a risk score showing the threat level and reasoning explaining why this particular pattern is concerning. This dramatically reduces alert fatigue because high-risk events are immediately distinguishable from benign anomalies. Operators can quickly triage incidents based on the Nova Lite classifications, understanding both what happened and why it matters. Through VPC Lattice, external security operations centers can access this monitoring and threat intelligence for unified visibility across organizational boundaries. This is AI-assisted system defense that amplifies human judgment.",
    'firehose': "Kinesis Firehose automatically batches, compresses, and converts our event stream to Parquet format. This transformation reduces storage costs by eighty percent while making analytics queries ten times faster. It's data pipeline automation at its best.",
    's3-datalake': "The S3 Data Lake uses columnar Parquet files partitioned by date and hour. This design means analytical queries only scan relevant data, not the entire dataset. What could take minutes now runs in seconds.",
    'athena': "Athena brings SQL to our data lake without managing servers. Analysts can write standard SQL queries to explore patterns across months of data. The serverless model means we only pay for queries we run.",
    'ai-analytics': "The Analytics Dashboard transforms raw metrics into actionable insights. We track model performance trends, identify which AI responses users find most helpful, and measure the business impact of our guardrails. This enables evidence-based policy refinement - understanding which questions cause confusion, where AI assistance provides the most value, and how system performance evolves over time. Through VPC Lattice, enterprise analyst teams can access these dashboards for cross-organizational intelligence and strategic insights.",
    'cloudwatch': "CloudWatch is our central nervous system for observability. Logs from every service converge here. We can trace a single request across all systems, correlate errors with deployments, and set intelligent alarms that reduce alert fatigue.",
    'cloudwatch-dashboard': "The IT Dashboard provides technical teams with real-time operational metrics. Lambda cold starts, API Gateway throttling, Bedrock token consumption - everything that impacts performance is visible at a glance.",
    'subnet-vpc': "The GovCloud VPC is our security perimeter. All backend services run in private subnets with no direct internet access. VPC endpoints create private tunnels to AWS services, ensuring sensitive data never traverses the public internet. This architecture meets DoD SRG Impact Level 5 compliance requirements."
  };

  // Page-level narrations - key takeaways for each major section
  const pageNarrations: Record<string, string> = {
    'frontend': "The frontend layer represents the user's gateway to the PVQ system. Built with React, it provides an intuitive interface that makes complex security questionnaires manageable. Key takeaway: we prioritize user experience while maintaining strict security standards. Real-time validation and intelligent feedback reduce errors at the source, saving hours of back-and-forth corrections later in the process.",

    'lambda': "Our serverless compute layer demonstrates the power of microservices architecture. Each Lambda function is purpose-built and independently scalable - from FAQ assistance to field assist and beyond. This means when FAQ traffic spikes, only that service scales, keeping costs optimal. Key insight: containerized Lambdas allow us to package complex Python dependencies and even custom AI models, giving us the flexibility of traditional servers with the economics and scalability of serverless. A critical architectural decision is VPC Lattice for enterprise integration, which enables other DoD systems to consume our AI services and access our analytics data. External systems can leverage specific AI functions like FAQ RAG and Field Assist, access real-time NOC monitoring, or query our historical data lake. This transforms our system from an isolated application into a shared enterprise intelligence platform while maintaining strict security controls we'll discuss in the security section.",

    'bedrock': "AWS Bedrock is where artificial intelligence meets security and compliance. We don't just use one model - we strategically deploy three different foundation models, each optimized for specific tasks. Nova Pro handles deep reasoning for comprehensive reviews, Nova Lite provides rapid field assistance, and Titan transforms documents into searchable knowledge. The critical differentiator is our Guardrails layer, which automatically filters sensitive information and enforces policy compliance before any data reaches the models. This isn't just AI - it's governable, auditable, and secure AI.",

    'observability': "In a distributed system handling sensitive data, observability isn't optional - it's existential. Our three-layer approach combines CloudWatch for technical monitoring, DynamoDB for real-time operational metrics, and a dual analytics pipeline for both immediate dashboards and long-term trend analysis. What sets this apart is our integration of Nova Lite small language model for intelligent event assessment. Rather than relying solely on threshold-based alerts that flood operators with noise, we use AI to evaluate event patterns and provide risk-based prioritization. The model examines audit events, assesses threat levels, and provides explainable reasoning for its classifications. This means operators see alerts with context - not just what happened, but why it matters and how serious it is. The key innovations here are the separation of real-time and batch analytics, combined with selective AI evaluation that optimizes both cost and operator effectiveness. Operations teams get instant visibility with intelligent prioritization, while analysts can explore months of historical patterns without impacting production performance. Every request is traceable, every error is logged, and every anomaly triggers contextual, risk-assessed alerts.",

    'data-flow': "The data flow section reveals how information moves through our system in two parallel paths. The real-time path uses SNS for instant event publishing, DynamoDB streams for immediate processing, and streams for live metric aggregation - all optimized for sub-second response times. Simultaneously, the batch path uses Kinesis Firehose to convert streaming data into optimized Parquet files in S3, making historical analysis efficient and cost-effective. This dual-path architecture means we never sacrifice real-time responsiveness for analytical capability, or vice versa. It's the best of both worlds.",

    'business-value': "Let's talk about what this architecture actually delivers in business terms. We're not just processing forms - we're transforming how security clearances are validated. High accuracy in AI-assisted reviews means fewer human errors and faster processing times. Real-time audit trails provide unprecedented transparency for oversight and compliance. Cost-effective serverless infrastructure scales from ten to ten thousand users without infrastructure team intervention. Most importantly, this system establishes a foundation for continuous improvement - every user interaction, every AI decision, and every validation feeds back into making the system smarter and more efficient.",

    'highlights': "The architecture highlights distill our key technical decisions into four critical principles. First, serverless-first design eliminates infrastructure management overhead. Second, event-driven architecture ensures loose coupling and independent scalability. Third, dual-path analytics balances real-time visibility with deep historical analysis. Fourth, security and compliance are built into every layer, not bolted on afterward. These aren't just buzzwords - they're architectural constraints that guided every design decision, from choosing DynamoDB over traditional databases to implementing SNS fan-out patterns for event distribution.",

    'security': "Security in this system operates on defense-in-depth principles. Starting with AWS GovCloud, we add VPC isolation to ensure backend services never touch the public internet. VPC endpoints use PrivateLink to access AWS services without internet exposure. VPC Lattice provides the Zero Trust layer for enterprise integration - when external DoD systems access our capabilities, Lattice enforces IAM authentication at the service mesh level, completely separate from application logic. This means we can securely share AI services and analytics data across accounts and VPCs with granular, policy-driven access control. All inter-service traffic stays on the AWS private backbone with complete auditability and no VPC peering complexity. Bedrock Guardrails provide AI-level content filtering and PII redaction. Every API call is authenticated and authorized. All data is encrypted at rest and in transit. Comprehensive audit logging captures every action with immutable timestamps. But here's what makes this unique: security isn't a separate layer - it's woven into the architecture itself. The VPC isolation, the Lattice service policies, the guardrails integration, the audit trail automation - security is foundational, not bolted on after the fact. This creates a Zero Trust architecture that meets the strictest DoD security requirements while enabling enterprise-wide intelligence sharing.",

    'diagram': "The interactive architecture diagram you see here provides a visual map of how all these components connect and communicate. Each element is clickable to reveal detailed technical specifications and design rationale. What's particularly valuable is seeing the data flows - the arrows show you exactly how information moves from user input through AI processing to storage and analytics. If you'd like a deeper dive, this diagram has its own guided tour feature with voice narration that walks through each component individually. You can activate that tour using the controls on the diagram itself whenever you're ready."
  };

  // Define hotspots for the diagram - positioned based on actual diagram layout
  // Tour follows PI delivery sequence: PI5 → PI6 → Future
  const hotspots = [
    // ========== PI5 Components (Foundation) ==========

    // User
    {
      id: 'user',
      x: 1,
      y: 45,
      width: 6,
      height: 10,
      title: 'End User',
      description: 'Personnel completing PVQ forms. Accesses the system through web browser to submit questionnaire responses and receive AI-powered validation assistance.',
      color: 'blue',
      pi: 'PI5'
    },

    // Frontend
    {
      id: 'frontend',
      x: 16,
      y: 43,
      width: 8,
      height: 14,
      title: 'Frontend (React + NGINX)',
      description: 'React single-page application served by NGINX. Provides form UI, validation feedback, real-time AI assistance, and dashboard access. Hosted on container infrastructure.',
      color: 'blue',
      pi: 'PI5'
    },

    // API Gateway
    {
      id: 'api-gateway',
      x: 28,
      y: 43,
      width: 8,
      height: 14,
      title: 'Go API Task Container',
      description: 'Containerized Go API Gateway. Handles HTTP routing, authentication, request validation, and proxies to FastAPI backend. Provides REST endpoints for frontend.',
      color: 'purple',
      pi: 'PI5'
    },

    // Main Lambda/Container (FastAPI)
    {
      id: 'fastapi-container',
      x: 42,
      y: 35,
      width: 12,
      height: 30,
      title: 'FastAPI AI Container',
      description: 'Main application container running FastAPI. Hosts multiple AI endpoints: FAQ validation, Comprehensive Final Review (CFR), AI Field Assist, User Feedback collection, and Analytics processing.',
      color: 'purple',
      pi: 'PI5'
    },

    // AWS Bedrock
    {
      id: 'bedrock',
      x: 64,
      y: 16,
      width: 11,
      height: 24,
      title: 'AWS Bedrock',
      description: 'Foundation Models: Nova Pro (balanced performance), Nova Lite (fast responses), Titan (text embeddings). Includes Guardrails for content filtering and PII detection, plus KnowledgeBase RAG integration.',
      color: 'green',
      pi: 'PI5'
    },

    // ElasticSearch
    {
      id: 'elasticsearch',
      x: 80,
      y: 14,
      width: 12,
      height: 12,
      title: 'AWS ElasticSearch Serverless',
      description: 'Vector store for FAQ RAG (Retrieval-Augmented Generation). Stores document embeddings with k-NN search for semantic similarity. Provides context to Bedrock for accurate, policy-based responses.',
      color: 'green',
      pi: 'PI5'
    },

    // CloudWatch
    {
      id: 'cloudwatch',
      x: 72,
      y: 78,
      width: 8,
      height: 10,
      title: 'CloudWatch',
      description: 'Centralized observability. Aggregates logs from all services, collects custom metrics (Lambda duration, token usage, error rates), enables alarms, and provides log insights.',
      color: 'cyan',
      pi: 'PI5'
    },

    // CloudWatch IT Dashboard
    {
      id: 'cloudwatch-dashboard',
      x: 84,
      y: 78,
      width: 10,
      height: 10,
      title: 'CloudWatch IT Dashboard',
      description: 'Operational monitoring dashboard. Real-time charts for Lambda concurrency, API latency, Bedrock throttling, error rates, and custom business metrics with X-Ray distributed tracing.',
      color: 'cyan',
      pi: 'PI5'
    },

    // Subnet label / VPC
    {
      id: 'subnet-vpc',
      x: 15,
      y: 1,
      width: 10,
      height: 6,
      title: 'AWS GovCloud VPC',
      description: 'Private subnet in AWS GovCloud West region with VPC endpoints (PrivateLink). All backend traffic to Bedrock, DynamoDB, S3, and other AWS services stays within AWS private network. DoD SRG IL5 compliance, complete data residency.',
      color: 'red',
      pi: 'PI5'
    },

    // ========== PI6 Components (Observability & Analytics) ==========

    // SNS Topic
    {
      id: 'sns-topic',
      x: 61,
      y: 48,
      width: 6,
      height: 10,
      title: 'SNS Topic (Audit Events)',
      description: 'Event publishing hub. FastAPI publishes all validation events, user feedback, and guardrail triggers. SNS fans out to multiple subscribers: NOC Monitor Lambda (which uses Nova Lite SLM to intelligently classify events and assess risk), real-time storage (DynamoDB), and analytics pipeline (Firehose). This enables selective AI evaluation for cost optimization while maintaining comprehensive audit trails.',
      color: 'orange',
      pi: 'PI6'
    },

    // DynamoDB Audit Records
    {
      id: 'dynamodb-audit',
      x: 72,
      y: 38,
      width: 7,
      height: 10,
      title: 'DynamoDB (Audit Records)',
      description: 'Primary audit table. Stores all request traces, validation results, user feedback, guardrail events. Enables DynamoDB Streams for real-time processing and point-in-time recovery.',
      color: 'orange',
      pi: 'PI6'
    },

    // DynamoDB NOC Metrics
    {
      id: 'dynamodb-noc',
      x: 82,
      y: 38,
      width: 7,
      height: 10,
      title: 'DynamoDB (NOC Metrics)',
      description: 'Aggregated metrics and classifications table. Stores rolling averages, error rates, latency percentiles, and SLM-evaluated risk scores with classification reasoning. Each event includes Nova Lite assessment for intelligent alert prioritization. Updated by stream processor for real-time NOC dashboard queries with explainable threat levels.',
      color: 'orange',
      pi: 'PI6'
    },

    // Kinesis Firehose
    {
      id: 'firehose',
      x: 72,
      y: 60,
      width: 7,
      height: 10,
      title: 'Kinesis Firehose',
      description: 'Streaming ETL pipeline. Subscribes to SNS, buffers events (60s/1MB), transforms to Parquet format, compresses, and delivers to S3 Data Lake for historical analytics.',
      color: 'indigo',
      pi: 'PI6'
    },

    // S3 Data Lake
    {
      id: 's3-datalake',
      x: 82,
      y: 60,
      width: 6,
      height: 10,
      title: 'S3 Data Lake (Parquet)',
      description: 'Columnar storage optimized for analytics. Parquet format with Snappy compression, partitioned by date/hour. Stores all historical events for Athena queries and long-term analysis.',
      color: 'indigo',
      pi: 'PI6'
    },

    // Athena
    {
      id: 'athena',
      x: 90,
      y: 60,
      width: 5,
      height: 10,
      title: 'Athena (Analytics)',
      description: 'Serverless SQL query engine. Runs Presto queries on S3 Parquet data using Glue Data Catalog schema. Powers historical analysis, trend reports, and business intelligence.',
      color: 'indigo',
      pi: 'PI6'
    },

    // ========== Future Components (Enterprise Integration) ==========

    // VPC Lattice - Gateway that enables the next two dashboards
    {
      id: 'vpc-lattice',
      x: 42,
      y: 1,
      width: 10,
      height: 13,
      title: 'VPC Lattice Service Network',
      description: 'AI Services Mesh providing Zero Trust service-to-service connectivity for DoD system integration. Enforces IAM authentication, service-level access policies, and audit logging. Enables external systems to securely consume AI services (FAQ RAG, AI Field Assist, and future API capabilities) as well as access analytics data including NOC metrics and S3 Parquet data lake. All cross-account and cross-VPC access adheres to strict service policies with complete auditability, supporting security operations, analyst teams, and enterprise intelligence needs while reducing network complexity.',
      color: 'green',
      pi: 'Future'
    },

    // NOC/SOC Dashboard - enabled by VPC Lattice
    {
      id: 'noc-soc',
      x: 93,
      y: 38,
      width: 6,
      height: 10,
      title: 'NOC/SOC Dashboard',
      description: 'Network Operations Center dashboard with intelligent alert prioritization. Real-time monitoring with WebSocket updates, live charts, system health status, and SLM-evaluated risk-based alerts. Nova Lite provides explainable classifications (risk scores 0-100) with reasoning, enabling operators to quickly assess event severity and take appropriate action. Reduces alert fatigue through AI-assisted threat assessment. Accessible to external SOCs via VPC Lattice for enterprise-wide security visibility.',
      color: 'orange',
      pi: 'Future'
    },

    // AI Analytics Dashboard - enabled by VPC Lattice
    {
      id: 'ai-analytics',
      x: 96,
      y: 60,
      width: 5,
      height: 10,
      title: 'AI Analytics Dashboard',
      description: 'Executive analytics interface. Historical trends, governance KPIs, model performance metrics, cost analysis, user feedback patterns, and continuous improvement tracking. Accessible to enterprise analysts via VPC Lattice for cross-organizational intelligence and strategic insights.',
      color: 'indigo',
      pi: 'Future'
    }
  ];

  // PI Rollout tour stops - shown after individual component tour
  const piRolloutStops = [
    {
      id: 'pi6-rollout',
      title: 'PI5 Delivery',
      piFilters: { PI5: true, PI6: false, Future: false },
      narration: "Program Increment 6 delivers core AI-powered assistance for personnel security questionnaire completion. Users gain intelligent form validation that catches errors in real-time before submission, dramatically reducing rework cycles. Context-aware FAQ assistance provides instant answers to complex security clearance questions, pulling from authoritative policy documents with citations. AI field assist helps users understand what information is needed for each field, reducing ambiguity and improving data quality. Comprehensive AI review validates entire form submissions against policy requirements, identifying potential issues before manual review. This release establishes the foundation: transforming personnel security vetting from a manual, error-prone process into an AI-assisted workflow that's faster, more accurate, and less frustrating for applicants.",
      keyPoints: [
        "Real-Time AI Validation",
        "Policy-Compliant FAQ Answers",
        "Intelligent Field Assistance",
        "Significant Error Reduction"
      ]
    },
    {
      id: 'pi7-rollout',
      title: 'PI6 Delivery',
      piFilters: { PI5: false, PI6: true, Future: false },
      narration: "In addition to continued end-user AI enhancements, Program Increment 7 establishes comprehensive audit infrastructure and analytics pipeline. Every user interaction, AI decision, and system event is captured with complete traceability and immutable timestamps. The intelligent event publishing architecture uses AI to selectively evaluate risk levels, providing explainable classifications for high-priority events while maintaining full audit trails for compliance. Raw event data flows into real-time storage for operational queries, while simultaneously feeding an optimized analytics pipeline that transforms streaming data into columnar format for efficient historical analysis. This dual-path architecture ensures both immediate operational visibility and long-term analytical capability. The foundation is set for evidence-based policy refinement, compliance reporting, and data-driven continuous improvement, though external dashboard access awaits enterprise integration in future releases.",
      keyPoints: [
        "Further end-user AI enhancements",
        "Complete Audit Trail",
        "AI Risk Assessment",
        "Dual-Path Analytics",
        "Evidence-Based Governance"
      ]
    },
    {
      id: 'future-rollout',
      title: 'Future Capabilities',
      piFilters: { PI5: false, PI6: false, Future: true },
      narration: "Future capabilities enable enterprise-wide intelligence sharing through secure service mesh architecture. VPC Lattice provides the gateway for external systems to access three critical capability tiers. First, other applications, such as the Individual Engagement Portal (IEP) can leverage our AI services for their own personnel security workflows, eliminating redundant development and ensuring consistent policy application across agencies. Second, security operations centers access real-time monitoring dashboards and threat intelligence, gaining unified visibility into personnel security events across organizational boundaries with AI-evaluated risk assessments and explainable classifications. Third, analyst teams access executive analytics dashboards and query our data lake to identify enterprise-wide trends, correlate patterns across agencies, and inform strategic decisions about clearance processing improvements. All integration happens through zero-trust gateways with IAM authentication, service-level policies, and complete auditability. This transforms our system from a standalone application into a shared intelligence platform that elevates security posture across the entire DoD enterprise while maintaining strict data sovereignty and access controls.",
      keyPoints: [
        "Cross-Agency AI Sharing",
        "Unified SOC Visibility",
        "Enterprise Analytics",
        "Zero Trust Gateway"
      ]
    },
    {
      id: 'complete-system',
      title: 'Complete Enterprise Solution',
      piFilters: { PI5: true, PI6: true, Future: true },
      narration: "When fully deployed, this architecture delivers transformational capabilities for NBIS personnel security vetting within DCSA. Applicants experience dramatically faster, less frustrating questionnaire completion with intelligent guidance and instant policy answers. Error rates drop by a significant degree through real-time AI validation and contextual assistance. Security operators gain AI-enhanced threat detection with explainable risk assessments, enabling faster incident response and reduced alert fatigue. Executive stakeholders access rich analytics for evidence-based policy improvements and performance tracking. The entire DoD enterprise can leverage shared AI capabilities and cross-organizational intelligence to enhance security posture while maintaining strict compliance with DOD SRG IL5 requirements. This isn't just form automation - it's intelligent, observable, and shareable security clearance processing that sets a new standard for government AI systems, delivering measurable value to applicants, operators, analysts, and the broader defense community.",
      keyPoints: [
        "Intelligent Form Completion",
        "AI-Enhanced Security Ops",
        "Enterprise Intelligence",
        "DoD SRG Compliant to IL5"
      ]
    }
  ];

  const sections = [
    { id: 'frontend', title: 'Frontend', ref: useRef<HTMLDivElement>(null) },
    { id: 'lambda', title: 'Lambda', ref: useRef<HTMLDivElement>(null) },
    { id: 'bedrock', title: 'Bedrock AI', ref: useRef<HTMLDivElement>(null) },
    { id: 'observability', title: 'Observability', ref: useRef<HTMLDivElement>(null) },
    { id: 'data-flow', title: 'Data Flow', ref: useRef<HTMLDivElement>(null) },
    { id: 'business-value', title: 'Business Value', ref: useRef<HTMLDivElement>(null) },
    { id: 'security', title: 'Security', ref: useRef<HTMLDivElement>(null) },
    { id: 'highlights', title: 'Highlights', ref: useRef<HTMLDivElement>(null) },
    { id: 'diagram', title: 'Diagram', ref: useRef<HTMLDivElement>(null) },
  ];

  // Page tour speech function
  const speakPageSection = (text: string, onComplete?: () => void) => {
    window.speechSynthesis.cancel();

    // If muted, skip speaking but still call onComplete
    if (isMuted) {
      if (onComplete) {
        setTimeout(onComplete, 2000); // Short delay to allow reading
      }
      return;
    }

    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.88;
      utterance.pitch = 1.05;
      utterance.volume = 1.0;

      // Use selected voice if available
      if (selectedVoice) {
        const voice = availableVoices.find(v => v.name === selectedVoice);
        if (voice) {
          utterance.voice = voice;
        }
      }

      utterance.onend = () => {
        if (onComplete) {
          onComplete();
        }
      };

      pageSpeechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else if (onComplete) {
      onComplete();
    }
  };

  const navigateToPageSection = (index: number, onSpeechComplete?: () => void) => {
    if (index >= 0 && index < sections.length) {
      setCurrentPageTourIndex(index);
      setActiveSection(index);

      // Scroll to the section
      sections[index].ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Get the section ID and narration
      const sectionId = sections[index].id;
      const narration = pageNarrations[sectionId];

      if (narration) {
        speakPageSection(narration, onSpeechComplete);
      } else if (onSpeechComplete) {
        onSpeechComplete();
      }
    }
  };

  const advanceToNextPageSection = () => {
    const nextIndex = currentPageTourIndexRef.current + 1;
    if (nextIndex < sections.length && isPageTourPlayingRef.current) {
      // Add a 2 second pause between sections for comprehension
      pageTourIntervalRef.current = setTimeout(() => {
        navigateToPageSection(nextIndex, advanceToNextPageSection);
      }, 2000);
    } else {
      // Page tour completed
      setIsPageTourPlaying(false);
      setCurrentPageTourIndex(-1);
    }
  };

  const startPageTour = () => {
    setIsPageTourPlaying(true);
    const startIndex = currentPageTourIndex >= 0 ? currentPageTourIndex : 0;
    navigateToPageSection(startIndex, advanceToNextPageSection);
  };

  const pausePageTour = () => {
    setIsPageTourPlaying(false);
    if (pageTourIntervalRef.current) {
      clearTimeout(pageTourIntervalRef.current);
      pageTourIntervalRef.current = null;
    }
    window.speechSynthesis.cancel();
  };

  const stopPageTour = () => {
    pausePageTour();
    setCurrentPageTourIndex(-1);
  };

  const pageTourNext = () => {
    pausePageTour();
    const nextIndex = currentPageTourIndex + 1;
    if (nextIndex < sections.length) {
      navigateToPageSection(nextIndex);
    }
  };

  const pageTourPrevious = () => {
    pausePageTour();
    const prevIndex = currentPageTourIndex - 1;
    if (prevIndex >= 0) {
      navigateToPageSection(prevIndex);
    }
  };

  // Scroll to section
  const scrollToSection = (index: number) => {
    sections[index].ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Navigate to previous/next section
  const navigateToPrevious = () => {
    if (activeSection > 0) {
      scrollToSection(activeSection - 1);
    }
  };

  const navigateToNext = () => {
    if (activeSection < sections.length - 1) {
      scrollToSection(activeSection + 1);
    }
  };

  // Detect active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for better detection

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i].ref.current;
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(i);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Export to PDF
  const exportToPDF = async () => {
    if (!contentRef.current) return;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 10; // Margin in mm
    const contentWidth = pageWidth - (margin * 2);
    let currentY = margin;

    // Add title page
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PVQ System Architecture', pageWidth / 2, 40, { align: 'center' });

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Comprehensive overview of the PVQ AI-powered', pageWidth / 2, 55, { align: 'center' });
    pdf.text('validation system with real-time monitoring', pageWidth / 2, 65, { align: 'center' });
    pdf.text('and analytics', pageWidth / 2, 75, { align: 'center' });

    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 90, { align: 'center' });
    pdf.setTextColor(0, 0, 0);

    // Define page groupings: [section indices for each page]
    // Page 1: sections 0,1,2 (Frontend, Lambda, Bedrock AI)
    // Page 2: sections 3,4 (Observability, Data Flow)
    // Page 3: sections 5,6,7 (Business Value, Highlights, Security)
    // Page 4: section 8 (Diagram)
    const pageGroups = [
      [0, 1, 2],  // Page 1
      [3, 4],      // Page 2
      [5, 6, 7],   // Page 3
      [8]          // Page 4
    ];

    // Process each page group
    for (let pageIndex = 0; pageIndex < pageGroups.length; pageIndex++) {
      const sectionIndices = pageGroups[pageIndex];

      // Add a new page (except for the first content page after title)
      pdf.addPage();
      currentY = margin;

      // Process each section in this page group
      for (let i = 0; i < sectionIndices.length; i++) {
        const sectionIndex = sectionIndices[i];
        const sectionElement = sections[sectionIndex].ref.current;
        if (!sectionElement) continue;

        // Capture the section as an image
        const canvas = await html2canvas(sectionElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        // Check if adding this section would exceed the page
        if (currentY + imgHeight > pageHeight - margin) {
          // If this is the first section on the page and it's too tall, we need to split it
          if (i === 0) {
            let remainingHeight = imgHeight;
            let sourceY = 0;

            while (remainingHeight > 0) {
              const availableHeight = pageHeight - currentY - margin;
              const heightToUse = Math.min(remainingHeight, availableHeight);

              // Calculate the portion of the source image to use
              const sourceHeight = (heightToUse / imgHeight) * canvas.height;

              // Create a temporary canvas with just this portion
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = canvas.width;
              tempCanvas.height = sourceHeight;
              const tempCtx = tempCanvas.getContext('2d');

              if (tempCtx) {
                tempCtx.drawImage(
                  canvas,
                  0, sourceY, canvas.width, sourceHeight,
                  0, 0, canvas.width, sourceHeight
                );

                const tempImgData = tempCanvas.toDataURL('image/png');
                pdf.addImage(tempImgData, 'PNG', margin, currentY, contentWidth, heightToUse);
              }

              remainingHeight -= heightToUse;
              sourceY += sourceHeight;

              if (remainingHeight > 0) {
                pdf.addPage();
                currentY = margin;
              } else {
                currentY += heightToUse;
              }
            }
          } else {
            // Not the first section and doesn't fit - this shouldn't happen with our grouping
            // but handle it anyway by adding to next page
            pdf.addPage();
            currentY = margin;
            pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, imgHeight);
            currentY += imgHeight;
          }
        } else {
          // Section fits on current page
          pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, imgHeight);
          currentY += imgHeight + 5; // Add small spacing between sections
        }
      }
    }

    pdf.save('PVQ-System-Architecture.pdf');
  };

  // Zoom and pan handlers
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    const container = diagramContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(1, zoom * delta), 5);

    // Calculate new pan to keep mouse position stable
    const zoomRatio = newZoom / zoom;
    const newPanX = mouseX - (mouseX - pan.x) * zoomRatio;
    const newPanY = mouseY - (mouseY - pan.y) * zoomRatio;

    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const zoomIn = () => {
    const newZoom = Math.min(zoom * 1.2, 5);
    setZoom(newZoom);
  };

  const zoomOut = () => {
    const newZoom = Math.max(zoom * 0.8, 1);
    setZoom(newZoom);
    if (newZoom === 1) {
      setPan({ x: 0, y: 0 });
    }
  };

  // Get color classes for hotspot
  const getHotspotColors = (color: string) => {
    const colorMap: Record<string, { border: string; bg: string; text: string }> = {
      blue: { border: 'border-blue-500', bg: 'bg-blue-500', text: 'text-blue-900' },
      purple: { border: 'border-purple-500', bg: 'bg-purple-500', text: 'text-purple-900' },
      green: { border: 'border-green-500', bg: 'bg-green-500', text: 'text-green-900' },
      orange: { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-orange-900' },
      indigo: { border: 'border-indigo-500', bg: 'bg-indigo-500', text: 'text-indigo-900' },
      cyan: { border: 'border-cyan-500', bg: 'bg-cyan-500', text: 'text-cyan-900' },
      red: { border: 'border-red-500', bg: 'bg-red-500', text: 'text-red-900' }
    };
    return colorMap[color] || colorMap.blue;
  };

  // Check if hotspot should be dimmed based on PI filters
  const shouldDimHotspot = (hotspotPi: string) => {
    if (hotspotPi === 'PI5' && !piFilters.PI5) return true;
    if (hotspotPi === 'PI6' && !piFilters.PI6) return true;
    if (hotspotPi === 'Future' && !piFilters.Future) return true;
    return false;
  };

  // Get PI badge color
  const getPiBadgeColor = (pi: string) => {
    if (pi === 'PI5') return 'bg-blue-600 text-white';
    if (pi === 'PI6') return 'bg-purple-600 text-white';
    if (pi === 'Future') return 'bg-green-600 text-white';
    return 'bg-gray-600 text-white';
  };

  // Speak narration using Web Speech API
  const speak = (text: string, onComplete?: () => void) => {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // If muted, skip speaking but still call onComplete
    if (isMuted) {
      if (onComplete) {
        setTimeout(onComplete, 2000); // Short delay to allow reading key points
      }
      return;
    }

    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.88; // Natural speaking pace
      utterance.pitch = 1.05; // Slightly higher pitch for warmth
      utterance.volume = 1.0;

      // Use selected voice if available
      if (selectedVoice) {
        const voice = availableVoices.find(v => v.name === selectedVoice);
        if (voice) {
          utterance.voice = voice;
        }
      }

      // Call onComplete when speech finishes
      utterance.onend = () => {
        if (onComplete) {
          onComplete();
        }
      };

      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else if (onComplete) {
      // If speech synthesis not available, still call onComplete
      setTimeout(onComplete, 3000);
    }
  };

  // Stop speech
  const stopSpeech = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  // Navigate to specific hotspot or PI rollout stop in tour
  const navigateToHotspot = (index: number, onSpeechComplete?: () => void) => {
    const totalStops = hotspots.length + piRolloutStops.length;

    if (index >= 0 && index < totalStops) {
      setCurrentTourIndex(index);

      // Check if this is a regular hotspot or PI rollout stop
      if (index < hotspots.length) {
        // Regular hotspot
        setActiveHotspot(hotspots[index].id);

        // Speak the narration
        const narration = narrations[hotspots[index].id];
        if (narration) {
          speak(narration, onSpeechComplete);
        } else if (onSpeechComplete) {
          setTimeout(onSpeechComplete, 2000);
        }
      } else {
        // PI Rollout stop
        const rolloutIndex = index - hotspots.length;
        const rolloutStop = piRolloutStops[rolloutIndex];

        // Clear active hotspot highlighting
        setActiveHotspot(null);

        // Set PI filters for this rollout stop
        setPiFilters(rolloutStop.piFilters);

        // Speak the rollout narration
        if (rolloutStop.narration) {
          speak(rolloutStop.narration, onSpeechComplete);
        } else if (onSpeechComplete) {
          setTimeout(onSpeechComplete, 2000);
        }
      }
    }
  };

  // Start auto-play tour
  const startTour = () => {
    setIsPlaying(true);
    const startIndex = currentTourIndex >= 0 ? currentTourIndex : 0;
    navigateToHotspot(startIndex, advanceToNext);
  };

  // Pause tour
  const pauseTour = () => {
    setIsPlaying(false);
    stopSpeech();
    if (playIntervalRef.current) {
      clearTimeout(playIntervalRef.current);
      playIntervalRef.current = null;
    }
  };

  // Stop tour completely
  const stopTour = () => {
    pauseTour();
    setCurrentTourIndex(-1);
    setActiveHotspot(null);
    // Reset PI filters to show all
    setPiFilters({ PI5: true, PI6: true, Future: true });
  };

  // Manual navigation - goes to next/previous
  const tourNext = () => {
    pauseTour(); // Pause auto-play when user manually navigates
    const totalStops = hotspots.length + piRolloutStops.length;
    const nextIndex = currentTourIndex + 1;
    if (nextIndex < totalStops) {
      navigateToHotspot(nextIndex);
    }
  };

  const tourPrevious = () => {
    pauseTour(); // Pause auto-play when user manually navigates
    const prevIndex = currentTourIndex - 1;
    if (prevIndex >= 0) {
      navigateToHotspot(prevIndex);
    } else {
      // If going back before start, reset PI filters
      setPiFilters({ PI5: true, PI6: true, Future: true });
    }
  };

  // Use ref to track playing state to avoid stale closures
  const isPlayingRef = useRef(isPlaying);
  const currentTourIndexRef = useRef(currentTourIndex);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
    currentTourIndexRef.current = currentTourIndex;
  }, [isPlaying, currentTourIndex]);

  // Sync page tour refs with state
  useEffect(() => {
    isPageTourPlayingRef.current = isPageTourPlaying;
    currentPageTourIndexRef.current = currentPageTourIndex;
  }, [isPageTourPlaying, currentPageTourIndex]);

  // Auto-advance to next component after speech completes
  const advanceToNext = () => {
    const nextIndex = currentTourIndexRef.current + 1;
    const totalStops = hotspots.length + piRolloutStops.length;

    if (nextIndex < totalStops && isPlayingRef.current) {
      // Add a 1.5 second pause between stops for comprehension
      // Use longer pause (3s) when transitioning from hotspots to PI rollout
      const pauseDuration = nextIndex === hotspots.length ? 3000 : 1500;

      playIntervalRef.current = setTimeout(() => {
        navigateToHotspot(nextIndex, advanceToNext);
      }, pauseDuration);
    } else {
      // Tour completed - reset to all filters enabled
      setIsPlaying(false);
      setCurrentTourIndex(-1);
      setActiveHotspot(null);
      setPiFilters({ PI5: true, PI6: true, Future: true });
    }
  };

  // Load voices on component mount
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          // Filter to English voices only
          const englishVoices = voices.filter(v => v.lang.startsWith('en'));
          setAvailableVoices(englishVoices);

          // Load saved voice preference or set default
          const savedVoice = localStorage.getItem('tour-voice-preference');
          if (savedVoice && englishVoices.find(v => v.name === savedVoice)) {
            setSelectedVoice(savedVoice);
          } else if (englishVoices.length > 0) {
            // Default to Google US English
            const defaultVoice = englishVoices.find(v => v.name.includes('Google US English'));
            setSelectedVoice(defaultVoice ? defaultVoice.name : englishVoices[0].name);
          }
        }
      };

      // Load voices immediately
      loadVoices();

      // Some browsers need this event to populate voices
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);

      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      };
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSpeech();
      if (playIntervalRef.current) {
        clearTimeout(playIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50 relative">
      {/* Slide-out Navigation from Right */}
      <div className={`fixed right-0 top-0 h-screen z-50 transition-transform duration-300 ease-in-out ${isNavCollapsed ? 'translate-x-full' : 'translate-x-0'}`}>
        <div className="h-full bg-white shadow-2xl border-l border-gray-200 w-72 flex flex-col">
          {/* Header with collapse button */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
            <span className="text-white font-semibold">Page Tour & Navigation</span>
            <button
              onClick={() => setIsNavCollapsed(!isNavCollapsed)}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Page Tour Controls */}
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Page Tour</h3>
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={pageTourPrevious}
                  disabled={currentPageTourIndex <= 0}
                  className={`p-2 rounded transition-colors ${
                    currentPageTourIndex <= 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title="Previous section"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {!isPageTourPlaying ? (
                  <button
                    onClick={startPageTour}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Tour
                  </button>
                ) : (
                  <button
                    onClick={pausePageTour}
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pause
                  </button>
                )}

                {currentPageTourIndex >= 0 && (
                  <button
                    onClick={stopPageTour}
                    className="p-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                    title="Stop tour"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                  </button>
                )}

                <button
                  onClick={pageTourNext}
                  disabled={currentPageTourIndex >= sections.length - 1}
                  className={`p-2 rounded transition-colors ${
                    currentPageTourIndex >= sections.length - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title="Next section"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              {currentPageTourIndex >= 0 && (
                <p className="text-xs text-gray-600">
                  Section {currentPageTourIndex + 1} of {sections.length}: {sections[currentPageTourIndex]?.title}
                </p>
              )}
            </div>

            {/* Section Links */}
            <div className="p-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-2">Sections</h3>
              {sections.map((section, index) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(index)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors border-l-4 rounded-r mb-1 ${
                    activeSection === index
                      ? 'bg-blue-50 border-blue-600 text-blue-900 font-semibold'
                      : currentPageTourIndex === index
                      ? 'bg-green-50 border-green-600 text-green-900'
                      : 'border-transparent text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {section.title}
                  {currentPageTourIndex === index && (
                    <span className="ml-2 text-xs text-green-600">● Playing</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="border-t border-gray-200 p-3 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={navigateToPrevious}
                disabled={activeSection === 0}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeSection === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                ← Prev
              </button>
              <button
                onClick={navigateToNext}
                disabled={activeSection === sections.length - 1}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeSection === sections.length - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next →
              </button>
            </div>

            {/* PDF Export Button */}
            <button
              onClick={exportToPDF}
              className="w-full px-3 py-2 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tab to open slide-out menu when collapsed */}
      {isNavCollapsed && (
        <button
          onClick={() => setIsNavCollapsed(false)}
          className="fixed right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-6 rounded-l-lg shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all z-40"
          title="Open navigation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <div ref={contentRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">PVQ.ai System Overview</h1>
          <p className="text-lg text-gray-600">
            Comprehensive walk-through of the PVQ AI-powered validation system with real-time monitoring and analytics
          </p>
        </div>

        {/* Architecture Diagram */}
        <div className="space-y-6">

          {/* Layer 1: User Interface */}
          <div ref={sections[0].ref} id="frontend" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">PVQ.ai Frontend Application</h2>
                <p className="text-gray-600">React-based user interface with AI enablement</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-16">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">eApp Application</h3>
                <p className="text-sm text-blue-700">PVQ/SF form sections, validation, and submission</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">AI Interactions</h3>
                 <p className="text-sm text-blue-700">FAQ, AI Field Assist, Comprehensive Final Review</p>
                <p className="text-sm text-blue-700">User ratings and feedback collection</p>
              </div>
              
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {/* Layer 2: API Gateway & Lambda */}
          <div ref={sections[1].ref} id="lambda" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">FastAPI + AWS Lambda</h2>
                <p className="text-gray-600">Serverless compute with containerized Lambda functions</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ml-16">
              <Link
                to="/foreign-contact"
                className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors duration-200 border-2 border-transparent hover:border-purple-300 cursor-pointer"
              >
                <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                  FAQ Lambda
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </h3>
                <p className="text-sm text-purple-700">Handles PVQ validation requests with AI assistance</p>
                <p className="text-xs text-purple-600 mt-2 font-medium">Try it now →</p>
              </Link>

              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                <h3 className="font-semibold text-purple-900 mb-2">AI Field Assist Lambda</h3>
                <p className="text-sm text-purple-700 mb-3">Real-time AI assistance for form field completion</p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/passport"
                    className="text-xs bg-purple-200 text-purple-900 px-2 py-1 rounded hover:bg-purple-300 transition-colors"
                  >
                    Try Passport →
                  </Link>
                  <Link
                    to="/federal-service"
                    className="text-xs bg-purple-200 text-purple-900 px-2 py-1 rounded hover:bg-purple-300 transition-colors"
                  >
                    Try Federal →
                  </Link>
                  <Link
                    to="/financial-judgment"
                    className="text-xs bg-purple-200 text-purple-900 px-2 py-1 rounded hover:bg-purple-300 transition-colors"
                  >
                    Try Financial →
                  </Link>
                </div>
              </div>

              <Link
                to="/review"
                className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors duration-200 border-2 border-transparent hover:border-purple-300 cursor-pointer"
              >
                <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                  Comprehensive Review Lambda
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </h3>
                <p className="text-sm text-purple-700">AI-powered final review and validation</p>
                <p className="text-xs text-purple-600 mt-2 font-medium">Try it now →</p>
              </Link>

              <Link
                to="/foreign-contact"
                className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors duration-200 border-2 border-transparent hover:border-purple-300 cursor-pointer"
              >
                <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                  Feedback Lambda
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </h3>
                <p className="text-sm text-purple-700">Processes user feedback and ratings</p>
                <p className="text-xs text-purple-600 mt-2 font-medium">Try it now →</p>
              </Link>
            </div>

            {/* VPC Lattice Integration */}
            <div className="mt-6 ml-16 bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border-2 border-green-200">
              <div className="flex items-start">
                <div className="bg-green-100 p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                    VPC Lattice Service Network
                    <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">NEW</span>
                  </h3>
                  <p className="text-sm text-green-800 mb-2">
                    Secure service mesh enabling DoD system integration. VPC Lattice provides Zero Trust access control with IAM-based service policies, allowing external systems to consume AI services (FAQ RAG, AI Field Assist, future APIs) and access analytics data (NOC metrics, S3 Parquet data lake) across accounts and VPCs with complete auditability, supporting security operations, analyst teams, and enterprise intelligence needs while reducing network complexity.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-green-700">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Zero Trust IAM Auth
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Private Connectivity
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Full Audit Trail
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {/* Layer 3: AI/ML Services */}
          <div ref={sections[2].ref} id="bedrock" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">AWS Bedrock AI Services</h2>
                <p className="text-gray-600">Foundation models with safety guardrails and RAG</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-16">
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Bedrock Guardrails</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Content filtering</li>
                  <li>• PII detection/redaction</li>
                  <li>• Prompt injection protection</li>
                  <li>• Policy enforcement</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Foundation Models</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Anthropic Claude family</li>
                  <li>• AWS Nova Pro & Lite</li>
                  <li>• AWS Titan (embeddings)</li>
                  <li>• Natural language processing</li>
                  <li>• Contextual understanding</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Knowledge Bases (RAG)</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• S3 document storage</li>
                  <li>• Vector embeddings</li>
                  <li>• OpenSearch indexing</li>
                  <li>• Semantic retrieval</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {/* Observability & Monitoring */}
          <div ref={sections[3].ref} id="observability" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-cyan-500">
            <div className="flex items-center mb-4">
              <div className="bg-cyan-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Observability & Monitoring</h2>
                <p className="text-gray-600">Comprehensive system instrumentation and LLM observability</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-16">
              {/* CloudWatch */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                  CloudWatch
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Centralized logging</li>
                  <li>• Custom metrics</li>
                  <li>• Real-time dashboards</li>
                  <li>• Alarms and alerts</li>
                </ul>
              </div>

              {/* X-Ray Tracing */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  X-Ray Tracing
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Distributed tracing</li>
                  <li>• Request flow visualization</li>
                  <li>• Performance bottlenecks</li>
                  <li>• Service map</li>
                </ul>
              </div>

              {/* OpenLLMetry */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  OpenLLMetry
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• LLM-specific telemetry</li>
                  <li>• Token usage tracking</li>
                  <li>• Model cost analysis</li>
                  <li>• Prompt/response tracing</li>
                </ul>
              </div>

              {/* SLM Event Evaluation */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-lg border-2 border-cyan-300 shadow-sm">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  SLM Event Evaluation
                  <span className="ml-2 text-xs bg-cyan-200 text-cyan-800 px-2 py-1 rounded-full">Nova Lite</span>
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Intelligent risk assessment (0-100)</li>
                  <li>• Explainable classifications</li>
                  <li>• Cost-optimized (selective eval)</li>
                  <li>• Reduces alert fatigue</li>
                </ul>
              </div>

              {/* Custom Instrumentation */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  Custom Metrics
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Validation success rates</li>
                  <li>• Guardrail trigger rates</li>
                  <li>• User feedback scores</li>
                  <li>• API latency percentiles</li>
                </ul>
              </div>

              {/* Integration Points */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Data Integration
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Streams to DynamoDB</li>
                  <li>• Firehose to Data Lake</li>
                  <li>• Athena for analytics</li>
                  <li>
                    <Link to="/noc" className="text-cyan-600 hover:text-cyan-800 underline font-medium">
                      View NOC Dashboard →
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Dashboards */}
              <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Visualization
                </h3>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• CloudWatch dashboards</li>
                  <li>• Real-time metrics</li>
                  <li>
                    <Link to="/analytics" className="text-cyan-600 hover:text-cyan-800 underline font-medium">
                      Executive Analytics →
                    </Link>
                  </li>
                  <li>• Custom visualizations</li>
                </ul>
              </div>
            </div>

            {/* Key Observability Features */}
            <div className="mt-6 ml-16 bg-cyan-50 p-4 rounded-lg border border-cyan-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 text-cyan-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Full Stack Observability
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700"><strong>End-to-end tracing:</strong> From frontend to Bedrock responses</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700"><strong>Cost tracking:</strong> Per-request LLM token usage and costs</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700"><strong>Quality metrics:</strong> User ratings and feedback analysis</span>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Down - Split into two paths */}
          <div className="flex justify-center items-center space-x-8">
            <div className="flex flex-col items-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span className="text-sm font-semibold text-gray-600 mt-2">Real-time Path</span>
            </div>
            <div className="flex flex-col items-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span className="text-sm font-semibold text-gray-600 mt-2">Analytics Path</span>
            </div>
          </div>

          {/* Layer 4: Data Flow - Two Parallel Paths */}
          <div ref={sections[4].ref} id="data-flow" className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Real-time Analytics Path */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
              <div className="flex items-center mb-4">
                <div className="bg-orange-100 p-3 rounded-lg mr-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Real-time Monitoring</h2>
                  <p className="text-sm text-gray-600">Live operational intelligence</p>
                </div>
              </div>
              <div className="space-y-3 ml-16">
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-900 text-sm mb-1">1. SNS Topic</h4>
                  <p className="text-xs text-orange-700">Lambda publishes events to SNS</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-900 text-sm mb-1">2. DynamoDB Streams</h4>
                  <p className="text-xs text-orange-700">Write to DynamoDB, trigger streams</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border-2 border-orange-200">
                  <h4 className="font-semibold text-orange-900 text-sm mb-1">3. NOC Monitor + SLM Evaluation</h4>
                  <p className="text-xs text-orange-700">Nova Lite classifies events, assesses risk, provides reasoning</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border-2 border-orange-300">
                  <h4 className="font-semibold text-orange-900 text-sm mb-1">4. NOC Dashboard</h4>
                  <p className="text-xs text-orange-700">Real-time alerts and monitoring</p>
                </div>
              </div>
            </div>

            {/* Temporal Analytics Path */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-indigo-500">
              <div className="flex items-center mb-4">
                <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Data Lake Analytics</h2>
                  <p className="text-sm text-gray-600">Historical insights and trends</p>
                </div>
              </div>
              <div className="space-y-3 ml-16">
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 text-sm mb-1">1. SNS Topic</h4>
                  <p className="text-xs text-indigo-700">Same events from Lambda</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 text-sm mb-1">2. Kinesis Firehose</h4>
                  <p className="text-xs text-indigo-700">Stream processing and buffering</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 text-sm mb-1">3. Parquet Conversion</h4>
                  <p className="text-xs text-indigo-700">Columnar storage in S3 Data Lake</p>
                </div>
                <div className="flex justify-center">
                  <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg border-2 border-indigo-300">
                  <h4 className="font-semibold text-indigo-900 text-sm mb-1">4. Athena Queries</h4>
                  <p className="text-xs text-indigo-700">SQL analytics on Data Lake</p>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Down - Convergence */}
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {/* Layer 5: Business Value & Outcomes */}
          <div ref={sections[5].ref} id="business-value" className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-xl p-8 text-white">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold mb-2">Business Value & Outcomes</h2>
              <p className="text-blue-100">Driving excellence through data-driven insights</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-lg p-6 border border-white border-opacity-30">
                <div className="flex items-center mb-3">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <h3 className="text-xl font-bold">Governance</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li>• Audit trail and compliance</li>
                  <li>• Guardrail policy enforcement</li>
                  <li>• Security monitoring</li>
                  <li>• Regulatory adherence</li>
                </ul>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-lg p-6 border border-white border-opacity-30">
                <div className="flex items-center mb-3">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <h3 className="text-xl font-bold">Operational Insights</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li>• Real-time performance metrics</li>
                  <li>• System health monitoring</li>
                  <li>• User behavior analytics</li>
                  <li>• Capacity planning</li>
                </ul>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-lg p-6 border border-white border-opacity-30">
                <div className="flex items-center mb-3">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-xl font-bold">Continuous Improvement</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li>• Model performance tracking</li>
                  <li>• User feedback loop</li>
                  <li>• Quality assurance metrics</li>
                  <li>• Iterative refinement</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Security & Privacy Architecture */}
          <div ref={sections[6].ref} id="security" className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg shadow-lg p-6 border-2 border-red-200 mt-8">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Security & Privacy</h2>
                <p className="text-gray-600">Enterprise-grade data protection and isolation</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* VPC Endpoints & Data Residency */}
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-red-500">
                <div className="flex items-start mb-3">
                  <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">VPC Endpoints & Data Residency</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      All AWS service communication occurs through VPC endpoints, ensuring data never leaves the Virtual Private Cloud boundary.
                    </p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-2 ml-9">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span><strong>Private connectivity:</strong> Traffic between Lambda and Bedrock stays within AWS network</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span><strong>No internet exposure:</strong> Sensitive PVQ data never traverses public internet</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span><strong>Data residency compliance:</strong> All data remains in US GovCloud West region</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span><strong>Enhanced security:</strong> Reduced attack surface with private endpoints</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold mb-2">Official AWS Documentation:</p>
                  <a
                    href="https://docs.aws.amazon.com/vpc/latest/privatelink/vpc-endpoints.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline block mb-1"
                  >
                    AWS PrivateLink and VPC Endpoints →
                  </a>
                  <a
                    href="https://docs.aws.amazon.com/bedrock/latest/userguide/vpc-interface-endpoints.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline block"
                  >
                    Amazon Bedrock VPC Endpoints →
                  </a>
                </div>
              </div>

              {/* Bedrock Privacy Model */}
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-orange-500">
                <div className="flex items-start mb-3">
                  <svg className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">AWS Bedrock Session Isolation</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      Bedrock employs strict sandboxing to ensure complete isolation between user sessions and zero data retention.
                    </p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-2 ml-9">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong>Session sandboxing:</strong> Each invocation is isolated - no cross-contamination between users</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong>Zero retention:</strong> AWS does not store or use customer inputs/outputs for model training</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong>No model training:</strong> Your prompts and data never improve base models or benefit other customers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span><strong>Ephemeral processing:</strong> Session context cleared immediately after response</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold mb-2">Official AWS Documentation:</p>
                  <a
                    href="https://docs.aws.amazon.com/bedrock/latest/userguide/data-protection.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline block mb-1"
                  >
                    Amazon Bedrock Data Protection →
                  </a>
                  <a
                    href="https://aws.amazon.com/bedrock/faqs/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline block mb-1"
                  >
                    Amazon Bedrock FAQs (See "Security & Privacy") →
                  </a>
                  <a
                    href="https://docs.aws.amazon.com/bedrock/latest/userguide/security.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline block"
                  >
                    Amazon Bedrock Security →
                  </a>
                </div>
              </div>
            </div>

            {/* Additional Security Highlights */}
            <div className="mt-6 bg-white p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Key Security Features
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700">Encryption in transit (TLS 1.2+)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700">Encryption at rest (AES-256)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700">IAM role-based access control</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 text-lg">✓</span>
                  <span className="text-gray-700">CloudTrail audit logging</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features Summary (Architecture Highlights) */}
          <div ref={sections[7].ref} id="highlights" className="bg-white rounded-lg shadow-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Architecture Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Serverless</h3>
                  <p className="text-sm text-gray-600">No infrastructure management</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Scalable</h3>
                  <p className="text-sm text-gray-600">Automatic scaling on demand</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Secure</h3>
                  <p className="text-sm text-gray-600">Guardrails and encryption</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Observable</h3>
                  <p className="text-sm text-gray-600">Full audit and monitoring</p>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center my-6">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {/* Interactive Architecture Diagram with Zoom */}
          <div ref={sections[8].ref} id="diagram" className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-lg shadow-xl p-6 border-2 border-indigo-300 mt-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">System Architecture Diagram</h2>
                  <p className="text-gray-600">Visual overview with AWS GovCloud, VPC, and complete data flows</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href="/PVQ-System-Architecture.drawio"
                  download
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-3 mb-4 bg-white rounded-lg p-3 border border-indigo-200">
              <div className="flex items-center gap-2">
                <button
                  onClick={zoomOut}
                  disabled={zoom === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    zoom === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  }`}
                  title="Zoom Out"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <button
                  onClick={zoomIn}
                  disabled={zoom >= 5}
                  className={`p-2 rounded-lg transition-colors ${
                    zoom >= 5
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  }`}
                  title="Zoom In"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                </button>
                {zoom > 1 && (
                  <button
                    onClick={resetZoom}
                    className="p-2 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                    title="Reset Zoom"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Divider */}
              <div className="h-8 w-px bg-gray-300"></div>

              {/* Tour Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={tourPrevious}
                  disabled={currentTourIndex <= 0}
                  className={`p-2 rounded-lg transition-colors ${
                    currentTourIndex <= 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Previous Component"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {!isPlaying ? (
                  <button
                    onClick={startTour}
                    className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                    title="Start Guided Tour"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={pauseTour}
                    className="p-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                    title="Pause Tour"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {currentTourIndex >= 0 && (
                  <button
                    onClick={stopTour}
                    className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                    title="Stop Tour"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {/* Mute/Unmute Audio */}
                <button
                  onClick={() => {
                    const newMutedState = !isMuted;
                    setIsMuted(newMutedState);
                    // If muting, stop any currently playing audio
                    if (newMutedState) {
                      stopSpeech();
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    isMuted
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title={isMuted ? 'Unmute Tour Audio' : 'Mute Tour Audio'}
                >
                  {isMuted ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                <button
                  onClick={tourNext}
                  disabled={currentTourIndex >= (hotspots.length + piRolloutStops.length - 1) && currentTourIndex >= 0}
                  className={`p-2 rounded-lg transition-colors ${
                    currentTourIndex >= (hotspots.length + piRolloutStops.length - 1) && currentTourIndex >= 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Next Stop"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 text-center">
                {currentTourIndex >= 0 ? (
                  <>
                    <span className="text-sm font-medium text-gray-700">
                      Tour: {currentTourIndex + 1} / {hotspots.length + piRolloutStops.length}
                      {currentTourIndex >= hotspots.length && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">PI Rollout</span>
                      )}
                    </span>
                    <div className="mt-1">
                      <span className="text-xs text-green-600 flex items-center justify-center gap-1">
                        <svg className={`w-3 h-3 ${isPlaying ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {isPlaying
                          ? (isMuted ? 'Playing guided tour (audio muted)' : 'Playing guided tour with narration')
                          : 'Tour paused - use arrows to navigate'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium text-gray-700">
                      Zoom: {Math.round(zoom * 100)}%
                    </span>
                    <div className="mt-1">
                      {zoom > 1 ? (
                        <span className="text-xs text-gray-500">
                          Click and drag to pan, scroll to zoom
                        </span>
                      ) : (
                        <span className="text-xs text-indigo-600 flex items-center justify-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Hover over components for details or start tour
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Divider */}
              <div className="h-8 w-px bg-gray-300"></div>

              {/* Voice Selector */}
              <div className="flex items-center gap-2">
                <label htmlFor="voice-select" className="text-xs font-semibold text-gray-600">Voice:</label>
                <select
                  id="voice-select"
                  value={selectedVoice}
                  onChange={(e) => {
                    setSelectedVoice(e.target.value);
                    localStorage.setItem('tour-voice-preference', e.target.value);
                  }}
                  className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  title="Select narration voice"
                >
                  {availableVoices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div className="h-8 w-px bg-gray-300"></div>

              {/* PI Filter Toggles */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs font-semibold text-gray-600">PI:</span>
                <button
                  onClick={() => setPiFilters(prev => ({ ...prev, PI5: !prev.PI5 }))}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    piFilters.PI5
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  title="Toggle PI5 components"
                >
                  PI5
                </button>
                <button
                  onClick={() => setPiFilters(prev => ({ ...prev, PI6: !prev.PI6 }))}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    piFilters.PI6
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  title="Toggle PI6 components"
                >
                  PI6
                </button>
                <button
                  onClick={() => setPiFilters(prev => ({ ...prev, Future: !prev.Future }))}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    piFilters.Future
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  title="Toggle Future components"
                >
                  Future
                </button>
              </div>
            </div>

            {/* Diagram with Zoom and Pan */}
            <div style={{ position: 'relative' }}>
              <div
                className="bg-white rounded-lg border-2 border-indigo-200"
                style={{ height: '600px', position: 'relative', overflow: 'hidden' }}
              >
                <div
                  ref={diagramContainerRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default'
                  }}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <div
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                      transformOrigin: '0 0',
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                      width: '100%',
                      height: 'auto',
                      display: 'inline-block',
                      position: 'relative'
                    }}
                  >
                    <img
                      src="/ArchitecturalDiagram.png"
                      alt="PVQ System Architecture Diagram"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                      draggable={false}
                    />

                {/* PI Rollout Phase Indicator - shown during PI rollout tour stops */}
                {currentTourIndex >= hotspots.length && currentTourIndex >= 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '9%',
                      left: '16%',
                      zIndex: 50,
                      pointerEvents: 'none'
                    }}
                  >
                    <div className="bg-gradient-to-b from-indigo-600 to-purple-600 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-white max-w-xs">
                      <div className="text-center mb-2">
                        <div className="text-sm font-bold tracking-wide">
                          {piRolloutStops[currentTourIndex - hotspots.length]?.title}
                        </div>
                      </div>
                      <div className="space-y-1.5 text-xs">
                        {piRolloutStops[currentTourIndex - hotspots.length]?.keyPoints.map((point, idx) => (
                          <div key={idx} className="flex items-start">
                            <span className="mr-2 opacity-75 flex-shrink-0">•</span>
                            <span className="font-medium text-left leading-tight">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Interactive Hotspots */}
                {hotspots.map((hotspot) => {
                  const colors = getHotspotColors(hotspot.color);
                  const isActive = activeHotspot === hotspot.id;
                  const isDimmed = shouldDimHotspot(hotspot.pi);

                  return (
                    <div
                      key={hotspot.id}
                      onMouseEnter={() => {
                        // Only allow manual hover if tour is not active
                        if (currentTourIndex < 0) {
                          setActiveHotspot(hotspot.id);
                        }
                      }}
                      onMouseLeave={() => {
                        // Only clear on hover leave if tour is not active
                        if (currentTourIndex < 0) {
                          setActiveHotspot(null);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        left: `${hotspot.x}%`,
                        top: `${hotspot.y}%`,
                        width: `${hotspot.width}%`,
                        height: `${hotspot.height}%`,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        zIndex: isActive ? 20 : 10
                      }}
                      className="group"
                    >
                      {/* Dimming Overlay for filtered PI components */}
                      {isDimmed && (
                        <div
                          className="absolute inset-0 bg-gray-900 bg-opacity-90 rounded transition-opacity duration-300 pointer-events-none"
                          style={{ zIndex: 5 }}
                        />
                      )}

                      {/* Hotspot Highlight Border */}
                      <div
                        className={`absolute inset-0 border-2 rounded transition-all duration-200 ${
                          isActive
                            ? currentTourIndex >= 0
                              ? `${colors.border} bg-white bg-opacity-30 shadow-2xl animate-pulse`
                              : `${colors.border} bg-white bg-opacity-20 shadow-lg`
                            : 'border-transparent hover:border-gray-400 hover:bg-white hover:bg-opacity-10'
                        }`}
                      />

                      {/* Tour mode indicator - pulsing ring */}
                      {isActive && currentTourIndex >= 0 && (
                        <div className="absolute inset-0 rounded">
                          <div className={`absolute inset-0 ${colors.border} rounded animate-ping opacity-40`}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

              {/* Tooltips - rendered outside overflow container */}
              {hotspots.map((hotspot) => {
                const colors = getHotspotColors(hotspot.color);
                const isActive = activeHotspot === hotspot.id;

                if (!isActive) return null;

                // Position CloudWatch tooltips above the icon to avoid clipping at bottom
                const positionAbove = hotspot.id === 'cloudwatch' || hotspot.id === 'cloudwatch-dashboard';

                // Offset CloudWatch IT Dashboard to the left to avoid right edge clipping
                const offsetLeft = hotspot.id === 'cloudwatch-dashboard';

                return (
                  <div
                    key={`tooltip-${hotspot.id}`}
                    className="absolute pointer-events-none"
                    style={{
                      left: positionAbove ? (offsetLeft ? 'auto' : `${hotspot.x}%`) : (hotspot.x < 50 ? `calc(${hotspot.x + hotspot.width}% + 8px)` : 'auto'),
                      right: positionAbove ? (offsetLeft ? `calc(100% - ${hotspot.x + hotspot.width}% + 8px)` : 'auto') : (hotspot.x >= 50 ? `calc(${100 - hotspot.x}% + 8px)` : 'auto'),
                      bottom: positionAbove ? `calc(100% - ${hotspot.y}% + 8px)` : 'auto',
                      top: positionAbove ? 'auto' : `${hotspot.y}%`,
                      zIndex: 50,
                      minWidth: '280px',
                      maxWidth: '320px'
                    }}
                  >
                    <div className={`bg-white rounded-lg shadow-2xl border-2 ${colors.border} overflow-hidden`}>
                      <div className={`${colors.bg} bg-opacity-10 px-4 py-3 border-b ${colors.border}`}>
                        <h4 className={`font-bold ${colors.text} flex items-center justify-between`}>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {hotspot.title}
                          </span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${getPiBadgeColor(hotspot.pi)}`}>
                            {hotspot.pi}
                          </span>
                        </h4>
                      </div>
                      <div className="px-4 py-3 bg-white">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {hotspot.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>

            {/* Diagram Legend and Info */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  AWS GovCloud West
                </h4>
                <p className="text-sm text-gray-700">Entire system deployed in secure government cloud region with VPC isolation</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Dual Data Paths
                </h4>
                <p className="text-sm text-gray-700">Real-time monitoring and historical analytics streams for comprehensive insights</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Private Connectivity
                </h4>
                <p className="text-sm text-gray-700">VPC endpoints ensure all traffic stays within AWS private network</p>
              </div>
            </div>

            {/* Quick Navigation to Components */}
            <div className="mt-6 bg-white p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-gray-900 mb-3">Explore System Components</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Link to="/passport" className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                  <span className="mr-1">→</span> Form Sections
                </Link>
                <Link to="/noc" className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                  <span className="mr-1">→</span> NOC Dashboard
                </Link>
                <Link to="/analytics" className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                  <span className="mr-1">→</span> Executive Analytics
                </Link>
                <Link to="/artifact-viewer" className="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                  <span className="mr-1">→</span> Audit Viewer
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SystemArchitecture;
